import {makeObservable, observable, action, computed} from "mobx";
import CommonStore from "../../stores/CommonStore";
import BillingPeriod from "../../entities/BillingPeriod";
import { BillingPeriodService } from "../../service/BillingPeriodService";
import { AgencyBillingClosureService } from "../../service/AgencyBillingClosureService";
import AgencyBillingClosureResponse from "../../model/billingPeriod/AgencyBillingClosureResponse";

class BillingPeriodClosureStore {
    commonStore: CommonStore;
    billingPeriodId: string;
    closures: AgencyBillingClosureResponse[] = [];
    filteredClosures: AgencyBillingClosureResponse[] = [];
    billingPeriod: BillingPeriod | null = null;
    isLoading: boolean = false;
    noZero: boolean = false;

    constructor(commonStore: CommonStore, billingPeriodId: string) {
        this.commonStore = commonStore;
        this.billingPeriodId = billingPeriodId;
        makeObservable(this, {
            closures: observable,
            filteredClosures: observable,
            billingPeriod: observable,
            isLoading: observable,
            loadData: action,
            setClosures: action,
            setBillingPeriod: action,
            setLoading: action,
            isEmpty: computed,
            totalAmount: computed
        });
    }

    async loadData() {
        this.setLoading(true);
        try {
            // Carregar período de faturação
            await this.loadBillingPeriod();
            // Carregar closures das agências
            await this.loadClosures();
        } catch (error) {
            console.error("Error loading billing period closure data:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async loadBillingPeriod() {
        try {
            const response = await BillingPeriodService.find(this.billingPeriodId);
            if (response.success && response.data) {
                this.setBillingPeriod(response.data);
            }
        } catch (error) {
            console.error("Error loading billing period:", error);
        }
    }

    async loadClosures() {
        try {
            const response = await AgencyBillingClosureService.getByBillingPeriodId(this.billingPeriodId);
            if (response.success && response.data) {
                this.setClosures(response.data);
            }
        } catch (error) {
            console.error("Error loading closures:", error);
        }
    }

    setClosures(closures: AgencyBillingClosureResponse[]) {
        this.closures = closures;
        this.filteredClosures = [...closures];
    }

    setBillingPeriod(billingPeriod: BillingPeriod) {
        this.billingPeriod = billingPeriod;
    }

    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setNoZeroValues(noZero: boolean): void {
        this.noZero = noZero;
        this.filteredClosures = noZero 
            ? this.closures.filter(closure => closure.totalAmount !== 0)
            : [...this.closures];
    }

    get isEmpty(): boolean {
        return this.closures.length === 0;
    }

    get totalAmount(): number {
        return this.closures.reduce((sum, closure) => sum + (closure.totalAmount || 0), 0);
    }
}

export default BillingPeriodClosureStore;
